import * as signalR from '@microsoft/signalr'
import { SIGNALR_EVENTS } from '~/constants/signalREvents'
import { getSignalRBaseURL } from '~/helpers/api'
import { useAuthStore } from '~/stores/auth'

const bcpHub: signalR.HubConnection = new signalR.HubConnectionBuilder()
  .withUrl(`${getSignalRBaseURL()}/export`)
  .configureLogging(signalR.LogLevel.Information)
  .withAutomaticReconnect()
  .build()
const notificationHub: signalR.HubConnection =
  new signalR.HubConnectionBuilder()
    .withUrl(`${getSignalRBaseURL()}/notification`)
    .configureLogging(signalR.LogLevel.Information)
    .withAutomaticReconnect()
    .build()
const inGroup = ref<boolean>(false)

export const start = async () => {
  try {
    await Promise.all(
      [bcpHub, notificationHub].map(async (hub) => {
        if ([signalR.HubConnectionState.Disconnected].includes(hub.state)) {
          return hub.start()
        }
      })
    )
  } catch (error) {
    console.error(error)
  }
}

export const stop = async () => {
  try {
    await bcpHub.stop()
    await notificationHub.stop()
    inGroup.value = false
  } catch (error) {
    console.error(error)
  }
}
const handleJoinGroup = async (userID: string) => {
  if (!userID) return
  await bcpHub.send('JoinGroup', userID)
  await notificationHub.send('JoinGroup', userID)
}
export const joinGroup = async () => {
  const AuthStore = useAuthStore()
  try {
    if (!inGroup.value && AuthStore.user?.loggedInUserID) {
      const isViewAsUser = AuthStore.isViewAsUser
      const userId = isViewAsUser
        ? AuthStore.user.loggedInUserID + AuthStore.user.viewAsUserID
        : AuthStore.user.loggedInUserID + AuthStore.user.loggedInUserID

      await handleJoinGroup(userId)
      inGroup.value = true
      return
    }
  } catch (error) {
    console.error(error)
  }
}

export function useSignalREvents() {
  return {
    on: ((event: string, fn: Function) => {
      // * When adding a new signalR event
      // * make sure that it will be picked up and registered to the correct hub
      // * by adding it to the corresponding hub events object in constants file.

      if (SIGNALR_EVENTS.NOTIFICATION_HUB_EVENT_NAMES.includes(event)) {
        notificationHub.on(event as never, fn as never)
      }

      if (SIGNALR_EVENTS.EXPORT_HUB_EVENT_NAMES.includes(event)) {
        bcpHub.on(event as never, fn as never)
      }

      // * The below unmounted doesn't appear to work very well
      // try detaching the event when the calling component gets unmounted
      tryOnUnmounted(() => {
        if (SIGNALR_EVENTS.NOTIFICATION_HUB_EVENT_NAMES.includes(event)) {
          notificationHub.off(event as never, fn as never)
        }
        if (SIGNALR_EVENTS.EXPORT_HUB_EVENT_NAMES.includes(event)) {
          bcpHub.off(event as never, fn as never)
        }
      })
    }) as unknown as typeof bcpHub.on,

    // * use existing off function
    off: (event: string, fn?: Function) => {
      if (SIGNALR_EVENTS.NOTIFICATION_HUB_EVENT_NAMES.includes(event)) {
        fn
          ? notificationHub.off(event as never, fn as never)
          : notificationHub.off(event)
      }
      if (SIGNALR_EVENTS.EXPORT_HUB_EVENT_NAMES.includes(event)) {
        fn ? bcpHub.off(event as never, fn as never) : bcpHub.off(event)
      }
    },

    get connectionID() {
      return {
        bcpHub: bcpHub.connectionId,
        notificationHub: notificationHub.connectionId,
      }
    },
  }
}
